import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { summarizePortfolio, toMonthKey } from "../lib/finance";
import {
  DEFAULT_COMPARE,
  DEFAULT_RECOVERY,
  DEFAULT_SETTINGS,
  STORAGE,
  demoLoans,
} from "./defaults";
import { initialRecovery, readLegacyCalculator } from "./migrate";
import { readJSON, readString, writeJSON, writeString } from "./storage";

const AppContext = createContext(null);

const CHART_TOKENS = [
  "series-1",
  "series-2",
  "series-3",
  "series-4",
  "series-5",
  "series-6",
  "series-neutral",
  "series-accent",
  "series-positive",
  "series-warning",
  "chart-grid",
  "chart-axis",
  "chart-ink",
  "chart-surface",
  "text-1",
  "text-2",
  "text-3",
];

function readChartTokens() {
  const cs = getComputedStyle(document.documentElement);
  const out = {};
  for (const t of CHART_TOKENS) out[t] = cs.getPropertyValue(`--${t}`).trim();
  return out;
}

export const uid = () =>
  Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-5);

function ensureUserId() {
  let id = readString(STORAGE.user);
  if (id == null) {
    // Same anonymous id scheme as the original app, so existing ids keep working.
    id = "dummy_user" + Math.random(11111, 99999);
    writeString(STORAGE.user, id);
  }
  return id;
}

function cleanLoan(l) {
  return {
    id: String(l.id || uid()),
    name: String(l.name || "Loan").slice(0, 60),
    type: l.type || "other",
    lender: String(l.lender || "").slice(0, 60),
    principal: Math.max(0, Number(l.principal) || 0),
    annualRate: Math.max(0, Number(l.annualRate) || 0),
    tenureMonths: Math.max(1, Math.round(Number(l.tenureMonths) || 12)),
    startYm: /^\d{4}-\d{2}$/.test(l.startYm || "") ? l.startYm : null,
    colorIndex: Number.isInteger(l.colorIndex) ? l.colorIndex % 6 : 0,
    strategy: l.strategy || null,
    createdAt: l.createdAt || Date.now(),
    updatedAt: l.updatedAt || Date.now(),
  };
}

function loadLoans() {
  const raw = readJSON(STORAGE.loans, []);
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((l) => l && Number(l.principal) > 0)
    .map(cleanLoan)
    .filter((l) => l.startYm);
}

function loadSettings() {
  const saved = readJSON(STORAGE.settings, {}) || {};
  const s = {
    ...DEFAULT_SETTINGS,
    ...saved,
    returns: { ...DEFAULT_SETTINGS.returns, ...(saved.returns || {}) },
    tax: { ...DEFAULT_SETTINGS.tax, ...(saved.tax || {}) },
  };
  if (!readString(STORAGE.migrated)) {
    const calc = readLegacyCalculator();
    if (calc && calc.inflationRate > 0) s.inflationRate = calc.inflationRate;
  }
  return s;
}

function nextColorIndex(loans) {
  const used = new Set(loans.map((l) => l.colorIndex));
  for (let i = 0; i < 6; i++) if (!used.has(i)) return i;
  return loans.length % 6;
}

function useSystemDark() {
  const query = "(prefers-color-scheme: dark)";
  const [dark, setDark] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const onChange = (e) => setDark(e.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () =>
      mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange);
  }, []);
  return dark;
}

export function AppProvider({ children }) {
  const [userId, setUserId] = useState(ensureUserId);
  const [displayName, setDisplayName] = useState(() => readString(STORAGE.displayName));
  const [loans, setLoans] = useState(loadLoans);
  const [settings, setSettings] = useState(loadSettings);
  const [recovery, setRecovery] = useState(() =>
    initialRecovery(readJSON(STORAGE.recovery, null))
  );
  const [compare, setCompare] = useState(() => ({
    ...DEFAULT_COMPARE,
    ...(readJSON(STORAGE.compare, {}) || {}),
  }));
  const [legacyCalc] = useState(readLegacyCalculator);
  const [toasts, setToasts] = useState([]);
  const nowKey = useMemo(() => toMonthKey(), []);

  // Mark migration done only after the initial reads above have used legacy data.
  useEffect(() => writeString(STORAGE.migrated, "1"), []);

  useEffect(() => writeJSON(STORAGE.loans, loans), [loans]);
  useEffect(() => writeJSON(STORAGE.settings, settings), [settings]);
  useEffect(() => writeJSON(STORAGE.recovery, recovery), [recovery]);
  useEffect(() => writeJSON(STORAGE.compare, compare), [compare]);

  /* ----- Theme ----- */
  const systemDark = useSystemDark();
  const resolvedTheme =
    settings.theme === "system" ? (systemDark ? "dark" : "light") : settings.theme;
  const [chartTokens, setChartTokens] = useState(readChartTokens);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", resolvedTheme);
    setChartTokens(readChartTokens());
  }, [resolvedTheme]);

  /* ----- Cloud mirror (same Firestore collection the original app wrote to) ----- */
  const syncedOnce = useRef(false);
  useEffect(() => {
    if (!syncedOnce.current && loans.length === 0) {
      syncedOnce.current = true;
      return undefined;
    }
    syncedOnce.current = true;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return undefined;
    const payload = {
      schema: 2,
      loans,
      recovery,
      assumptions: {
        inflationRate: settings.inflationRate,
        returns: settings.returns,
        tax: settings.tax,
      },
    };
    const t = setTimeout(() => {
      import("../api/LoanApi.js")
        .then((m) =>
          m.default.updateLoanDetails({ email: userId, data: JSON.stringify(payload) })
        )
        .catch(() => {});
    }, 4000);
    return () => clearTimeout(t);
  }, [loans, recovery, settings.inflationRate, settings.returns, settings.tax, userId]);

  /* ----- Toasts ----- */
  const dismissToast = useCallback((id) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message, { action, duration = 4000, tone = "success" } = {}) => {
      const id = uid();
      setToasts((ts) => [...ts.slice(-2), { id, message, action, tone }]);
      setTimeout(() => dismissToast(id), duration);
      return id;
    },
    [dismissToast]
  );

  /* ----- Loan actions ----- */
  const addLoan = useCallback((data) => {
    const id = uid();
    setLoans((ls) => [
      ...ls,
      cleanLoan({ ...data, id, colorIndex: nextColorIndex(ls), createdAt: Date.now() }),
    ]);
    return id;
  }, []);

  const updateLoan = useCallback((id, patch) => {
    setLoans((ls) =>
      ls.map((l) => (l.id === id ? cleanLoan({ ...l, ...patch, id, updatedAt: Date.now() }) : l))
    );
  }, []);

  const loansRef = useRef(loans);
  loansRef.current = loans;

  const deleteLoan = useCallback(
    (id) => {
      const index = loansRef.current.findIndex((l) => l.id === id);
      const removed = loansRef.current[index];
      if (!removed) return;
      setLoans((ls) => ls.filter((l) => l.id !== id));
      toast(`${removed.name} deleted`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: () =>
            setLoans((ls) => {
              if (ls.some((l) => l.id === removed.id)) return ls;
              const next = [...ls];
              next.splice(Math.min(index, next.length), 0, removed);
              return next;
            }),
        },
      });
    },
    [toast]
  );

  const saveStrategy = useCallback(
    (id, strategy) => updateLoan(id, { strategy }),
    [updateLoan]
  );

  const updateSettings = useCallback((patch) => {
    setSettings((s) => ({ ...s, ...(typeof patch === "function" ? patch(s) : patch) }));
  }, []);

  const updateRecovery = useCallback((patch) => {
    setRecovery((r) => ({ ...r, ...(typeof patch === "function" ? patch(r) : patch) }));
  }, []);

  const updateCompare = useCallback((patch) => {
    setCompare((c) => ({ ...c, ...(typeof patch === "function" ? patch(c) : patch) }));
  }, []);

  const exportBackup = useCallback(
    () => ({
      app: "loan-recovery-pro",
      schema: 2,
      exportedAt: new Date().toISOString(),
      loans,
      settings,
      recovery,
      compare,
    }),
    [loans, settings, recovery, compare]
  );

  const importBackup = useCallback((data) => {
    if (!data || data.app !== "loan-recovery-pro" || !Array.isArray(data.loans)) {
      throw new Error("This file isn't a Loan Recovery Pro backup.");
    }
    const nextLoans = data.loans
      .filter((l) => l && Number(l.principal) > 0)
      .map(cleanLoan)
      .filter((l) => l.startYm);
    setLoans(nextLoans);
    if (data.settings) {
      setSettings((s) => ({
        ...DEFAULT_SETTINGS,
        ...data.settings,
        theme: s.theme,
        returns: { ...DEFAULT_SETTINGS.returns, ...(data.settings.returns || {}) },
        tax: { ...DEFAULT_SETTINGS.tax, ...(data.settings.tax || {}) },
      }));
    }
    if (data.recovery) setRecovery(initialRecovery(data.recovery));
    if (data.compare) setCompare({ ...DEFAULT_COMPARE, ...data.compare });
    return nextLoans.length;
  }, []);

  const resetAll = useCallback(() => {
    setLoans([]);
    setSettings((s) => ({ ...DEFAULT_SETTINGS, theme: s.theme }));
    setRecovery({ ...DEFAULT_RECOVERY });
    setCompare({ ...DEFAULT_COMPARE });
  }, []);

  const signIn = useCallback((email, name) => {
    writeString(STORAGE.user, email);
    writeString(STORAGE.displayName, name || null);
    setUserId(email);
    setDisplayName(name || null);
  }, []);

  const signOut = useCallback(() => {
    writeString(STORAGE.user, null);
    writeString(STORAGE.displayName, null);
    setUserId(ensureUserId());
    setDisplayName(null);
  }, []);

  /* ----- Derived portfolio ----- */
  const isDemo = loans.length === 0 && settings.showDemo;
  const effectiveLoans = useMemo(
    () => (loans.length ? loans : settings.showDemo ? demoLoans(nowKey) : []),
    [loans, settings.showDemo, nowKey]
  );
  const portfolio = useMemo(
    () => summarizePortfolio(effectiveLoans, nowKey),
    [effectiveLoans, nowKey]
  );

  const value = {
    userId,
    displayName,
    signIn,
    signOut,
    nowKey,
    loans,
    effectiveLoans,
    isDemo,
    portfolio,
    addLoan,
    updateLoan,
    deleteLoan,
    saveStrategy,
    settings,
    updateSettings,
    resolvedTheme,
    chartTokens,
    recovery,
    updateRecovery,
    compare,
    updateCompare,
    legacyCalc,
    exportBackup,
    importBackup,
    resetAll,
    toasts,
    toast,
    dismissToast,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside <AppProvider>");
  return ctx;
}
